import { configureStore } from "@reduxjs/toolkit";
import storage from "redux-persist/lib/storage";
import { persistReducer, persistStore } from "redux-persist";

import reducer from "./reducer";
import {
  PREVIEW_MODE,
  previewAuthState,
  previewContentState,
} from "./preview/previewMode";

import { authApi } from "./Services/authServices";
import { textingApi } from "./Services/textingServices";
import { trainingApi } from "./Services/trainingServices";
import { salesApi } from "./Services/salesServices";
import { userApi } from "./Services/userServices";
import { storeApi } from "./Services/storeServices";
import { ordersApi } from "./Services/ordersServices";
import { tasksApi } from "./Services/tasksServices";
import { inventoryApi } from "./Services/inventoryServices";
import { statsApi } from "./Services/statsServices";
import { projectedEarningsApi } from "./Services/projectedEarnings";
import { payrollApi } from "./Services/payrollServices";
import { payrollAdminApi } from "./Services/payrollAdminServices";
import { deductionApi } from "./Services/deductionServices";
import { terminationApi } from "./Services/terminationServices";
import { trucksInformationApi } from "./Services/truckServices";
import { orgChartApi } from "./Services/orgChartServices";
import { tooltipApi } from "./Services/tooltipServices";
import { eventsApi } from "./Services/eventsServices";
import { leadsApi } from "./Services/leadsServices";
import { reviewsApi } from "./Services/reviewsServices";
import { tournamentApi } from "./Services/tournamentServices";
import { positionAiApi } from "./Services/positionAiServices";
import { nativeViewsApi } from "./Services/nativeViewsServices";
import { taskCommentApi } from "./Services/taskCommentServices";
import { fantasyApi } from "./Services/fantasyServices";
import { repLocationApi } from "./Services/repLocationServices";
import { positionChangeApi } from "./Services/positionChangeServices";
import { incentiveScoringApi } from "./Services/incentiveScoringServices";
import { vendorAdminApi } from "./Services/vendorAdminServices";
import { employeeAdminApi } from "./Services/employeeAdminServices";
import { equipmentAdminApi } from "./Services/equipmentAdminServices";
import { ptoApi } from "./Services/ptoServices";
import { sendLoginApi } from "./Services/sendLoginServices";
import { invoicesDocumentsApi } from "./Services/invoicesDocumentsServices";
import { manualApi } from "./Services/manualServices";
import { positionAdminApi } from "./Services/positionAdminServices";
import { inventoryAdminApi } from "./Services/inventoryAdminServices";
import { timeSheetApi } from "./Services/timeSheetServices";

const persistConfig = {
  key: "root",
  storage,
  whitelist: ["auth"],
};

const persistedReducer = persistReducer(persistConfig, reducer);

export const store = configureStore({
  reducer: persistedReducer,
  // Preview builds boot already "logged in" with a mock session so the auth
  // gate is bypassed without a login, and land directly on the changed page.
  // No-op in prod (PREVIEW_MODE is false).
  ...(PREVIEW_MODE
    ? { preloadedState: { auth: previewAuthState(), content: previewContentState() } }
    : {}),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          "persist/PERSIST",
          "persist/REHYDRATE",
          "persist/PURGE",
        ],
      },
    }).concat([
      authApi.middleware,
      textingApi.middleware,
      trainingApi.middleware,
      salesApi.middleware,
      userApi.middleware,
      storeApi.middleware,
      ordersApi.middleware,
      tasksApi.middleware,
      inventoryApi.middleware,
      statsApi.middleware,
      projectedEarningsApi.middleware,
      payrollApi.middleware,
      payrollAdminApi.middleware,
      deductionApi.middleware,
      trucksInformationApi.middleware,
      terminationApi.middleware,
      orgChartApi.middleware,
      tooltipApi.middleware,
      eventsApi.middleware,
      leadsApi.middleware,
      reviewsApi.middleware,
      tournamentApi.middleware,
      positionAiApi.middleware,
      nativeViewsApi.middleware,
      taskCommentApi.middleware,
      fantasyApi.middleware,
      repLocationApi.middleware,
      positionChangeApi.middleware,
      incentiveScoringApi.middleware,
      vendorAdminApi.middleware,
      employeeAdminApi.middleware,
      equipmentAdminApi.middleware,
      ptoApi.middleware,
      sendLoginApi.middleware,
      invoicesDocumentsApi.middleware,
      manualApi.middleware,
      positionAdminApi.middleware,
      inventoryAdminApi.middleware,
      timeSheetApi.middleware,
    ]),
});

export const persistor = persistStore(store);
